import React, { useMemo, useState } from "react"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import {
  faBoxOpen,
  faCalendar,
  faChevronDown,
  faClipboardList,
  faClose,
  faCoins,
  faCreditCard,
  faEye,
  faFileLines,
  faHashtag,
  faIdCard,
  faLocationDot,
  faMoneyBill,
  faMoneyBillWave,
  faMotorcycle,
  faPenToSquare,
  faPlus,
  faMinus,
  faPercent,
  faReceipt,
  faStore,
  faTags,
  faTruck,
  faUser,
  faPhone,
} from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitleChildrenPath, TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { convertToLocalMoney, parseDateWithoutTimezoneShift } from "../../../Objects/Generics/Helps"
import { ManualExternalOrder } from "../../../Objects/ManualExternalOrder/ManualExternalOrder"
import { ManualExternalOrderItem } from "../../../Objects/ManualExternalOrder/ManualExternalOrderItem"
import { EManualExternalOrderStatus } from "../../../Objects/ManualExternalOrder/EManualExternalOrderStatus"
import {
  getManualExternalOrderDeliveryLabel,
  getManualExternalOrderPaymentLabel,
  getManualExternalOrderReconciliationLabel,
  getManualExternalOrderSourceLabel,
  getManualExternalOrderStatusLabel,
  createEmptyManualExternalOrder,
  createManualExternalOrderDropdownOptions,
  getManualExternalOrderDropdownValue,
  manualExternalOrderDeliveryOptions,
  manualExternalOrderPaymentOptions,
  manualExternalOrderReconciliationOptions,
  manualExternalOrderSourceOptions,
  manualExternalOrderStatusOptions,
  previewManualExternalOrderTotals,
} from "../../../Objects/ManualExternalOrder/manualExternalOrderHelpers"
import { useManualExternalOrders } from "../../../hooks/ManualExternalOrder/useManualExternalOrders"
import { CountElements } from "../../../components/CountElements/CountElements"

interface ManualExternalOrderPopupProps {
  order?: ManualExternalOrder
  onClose: () => void
  onSave: () => void
  onCancelOrder: (order: ManualExternalOrder) => void
}

interface SectionHeaderProps {
  title: string
  icon: IconProp
  onToggle: () => void
  descriptionKey: string
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, icon, onToggle, descriptionKey }) => (
  <TitleChildrenPath
    title={title}
    icon={icon}
    iconColor={THEME.colors.iconBGSecondaryColor}
    boxShadow="none"
    animationTypes={AnimationTypes.slideUpIn}
    subtitleChildren={
      <ButtonIcon
        sizeNumber={SIZE.buttonExpandIcon}
        bgColor={THEME.colors.buttonBGExpandColor}
        iconColor={THEME.colors.buttonFGExpandColor}
        icon={faChevronDown}
        onClick={onToggle}
        size="small"
        descriptionKey={descriptionKey}
      />
    }
  />
)

export const ManualExternalOrderPopup: React.FC<ManualExternalOrderPopupProps> = ({
  order,
  onClose,
  onSave,
  onCancelOrder,
}) => {
  const { t } = useTranslation("manualExternalOrder")

  const { handleSaveManualExternalOrder } = useManualExternalOrders()

  const [form, setForm] = useState<ManualExternalOrder>(() => order ?? createEmptyManualExternalOrder())

  const [showAdvanced, setShowAdvanced] = useState(Boolean(order?.customerPhone || order?.deliveryAddress))

  const [showFinancial, setShowFinancial] = useState(Boolean(order?.platformFeeAmount || order?.discountAmount))

  const [showItems, setShowItems] = useState(Boolean(order?.items?.length))

  const [showRegister, setShowRegister] = useState(false)

  const totals = useMemo(() => previewManualExternalOrderTotals(form), [form])

  const isExisting = Boolean(order?.id)

  const isLocked = isExisting && order?.status === EManualExternalOrderStatus.Cancelled

  const patch = (data: Partial<ManualExternalOrder>) => setForm((current) => ({ ...current, ...data }))

  const handleMoneyChange = (key: keyof ManualExternalOrder, value: string) =>
    patch({ [key]: value.toNumber() } as Partial<ManualExternalOrder>)

  const handleItemChange = (index: number, item: Partial<ManualExternalOrderItem>) => {
    if (isLocked) {
      return
    }

    const nextItems = [...form.items]

    nextItems[index] = { ...nextItems[index], ...item }

    patch({ items: nextItems })
  }

  const handleAddItem = () => {
    if (isLocked) {
      return
    }

    patch({ items: [...form.items, { productName: "", quantity: 1, unitPrice: 0, totalPrice: 0 }] })
    setShowItems(true)
  }

  const handleRemoveItem = (index: number) => {
    if (isLocked) {
      return
    }

    patch({ items: form.items.filter((_, itemIndex) => itemIndex !== index) })
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (isLocked) {
      return
    }

    handleSaveManualExternalOrder(
      {
        ...form,
        productsAmount: totals.productsAmount,
        grossAmount: totals.grossAmount,
        netAmount: totals.netAmount,
      },
      () => {
        onSave()
        onClose()
      },
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <form onSubmit={handleSubmit} className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={order ? t("popup.edit_title") : t("popup.create_title")}
              icon={faClipboardList}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.button.onClose"
                />
              }
            />

            <section className="grid grid-cols-1 gap-3 p-2 md:grid-cols-3">
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs font-semibold text-slate-500">{t("totals.products")}</p>

                  <strong className="text-lg text-slate-800">{convertToLocalMoney(totals.productsAmount)}</strong>
                </div>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="rounded-xl bg-orange-50 p-3">
                  <p className="text-xs font-semibold text-orange-700">{t("totals.gross")}</p>

                  <strong className="text-lg text-orange-700">{convertToLocalMoney(totals.grossAmount)}</strong>
                </div>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="rounded-xl bg-emerald-50 p-3">
                  <p className="text-xs font-semibold text-emerald-700">{t("totals.net")}</p>

                  <strong className="text-lg text-emerald-700">{convertToLocalMoney(totals.netAmount)}</strong>
                </div>
              </Animate>
            </section>

            <div className="grid gap-4 mt-2">
              <section className="grid md:grid-cols-3 gap-3 rounded-lg border-2 border-orange-100 bg-orange-50/30 p-3">
                <InputField
                  disable={isLocked}
                  icon={faCalendar}
                  type="date"
                  required
                  value={parseDateWithoutTimezoneShift(form.orderDate).toInputDate()}
                  placeholder={t("fields.order_date")}
                  onChange={(event) => patch({ orderDate: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.orderDate"
                />

                <InputField
                  disable={isLocked}
                  icon={faMoneyBill}
                  type="number"
                  value={form.grossAmount.toStringOrEmpty()}
                  placeholder={t("fields.gross_amount")}
                  onChange={(event) => handleMoneyChange("grossAmount", event.target.value)}
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.grossAmount"
                />

                <InputField
                  disable={isLocked}
                  icon={faReceipt}
                  type="text"
                  value={form.externalOrderCode ?? ""}
                  placeholder={t("fields.external_code")}
                  onChange={(event) => patch({ externalOrderCode: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.externalCode"
                />

                <GenericDropDown
                  disable={isLocked}
                  initialState={getManualExternalOrderDropdownValue(form.status)}
                  data={createManualExternalOrderDropdownOptions(
                    manualExternalOrderStatusOptions,
                    getManualExternalOrderStatusLabel,
                  )}
                  handlerFuncion={(event) =>
                    patch({ status: event.target.value.toNumber() as ManualExternalOrder["status"] })
                  }
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.select.select"
                />

                <GenericDropDown
                  disable={isLocked}
                  initialState={getManualExternalOrderDropdownValue(form.sourceType)}
                  data={createManualExternalOrderDropdownOptions(
                    manualExternalOrderSourceOptions,
                    getManualExternalOrderSourceLabel,
                  )}
                  handlerFuncion={(event) =>
                    patch({ sourceType: event.target.value.toNumber() as ManualExternalOrder["sourceType"] })
                  }
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.select.select2"
                />

                <GenericDropDown
                  disable={isLocked}
                  initialState={getManualExternalOrderDropdownValue(form.paymentMethod)}
                  data={createManualExternalOrderDropdownOptions(
                    manualExternalOrderPaymentOptions,
                    getManualExternalOrderPaymentLabel,
                  )}
                  handlerFuncion={(event) =>
                    patch({ paymentMethod: event.target.value.toNumber() as ManualExternalOrder["paymentMethod"] })
                  }
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.select.select3"
                />
              </section>

              <section className="grid gap-3">
                <SectionHeader
                  title={t("sections.advanced")}
                  icon={faEye}
                  onToggle={() => setShowAdvanced((current) => !current)}
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.section.advanced"
                />

                {showAdvanced && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <div className="grid grid-cols-1 gap-3 p-2 border-2 rounded-lg md:grid-cols-3">
                      <InputField
                        disable={isLocked}
                        icon={faUser}
                        type="text"
                        value={form.customerName ?? ""}
                        placeholder={t("fields.customer_name")}
                        onChange={(event) => patch({ customerName: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.customerName"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faPhone}
                        type="text"
                        value={form.customerPhone ?? ""}
                        placeholder={t("fields.customer_phone")}
                        onChange={(event) => patch({ customerPhone: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.customerPhone"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faIdCard}
                        type="text"
                        value={form.customerDocument ?? ""}
                        placeholder={t("fields.customer_document")}
                        onChange={(event) => patch({ customerDocument: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.customerDocument"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faLocationDot}
                        type="text"
                        value={form.deliveryAddress ?? ""}
                        placeholder={t("fields.delivery_address")}
                        onChange={(event) => patch({ deliveryAddress: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.deliveryAddress"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faLocationDot}
                        type="text"
                        value={form.neighborhood ?? ""}
                        placeholder={t("fields.neighborhood")}
                        onChange={(event) => patch({ neighborhood: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.neighborhood"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faLocationDot}
                        type="text"
                        value={form.city ?? ""}
                        placeholder={t("fields.city")}
                        onChange={(event) => patch({ city: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.city"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faLocationDot}
                        type="text"
                        value={form.state ?? ""}
                        placeholder={t("fields.state")}
                        onChange={(event) => patch({ state: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.state"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faLocationDot}
                        type="text"
                        value={form.zipCode ?? ""}
                        placeholder={t("fields.zip_code")}
                        onChange={(event) => patch({ zipCode: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.zipCode"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faMotorcycle}
                        type="text"
                        value={form.courierName ?? ""}
                        placeholder={t("fields.courier_name")}
                        onChange={(event) => patch({ courierName: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.courierName"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faStore}
                        type="text"
                        value={form.sourceName ?? ""}
                        placeholder={t("fields.source_name")}
                        onChange={(event) => patch({ sourceName: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.sourceName"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faFileLines}
                        type="text"
                        value={form.customerNotes ?? ""}
                        placeholder={t("fields.customer_notes")}
                        onChange={(event) => patch({ customerNotes: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.customerNotes"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faClipboardList}
                        type="text"
                        value={form.internalNotes ?? ""}
                        placeholder={t("fields.internal_notes")}
                        onChange={(event) => patch({ internalNotes: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.internalNotes"
                      />

                      <GenericDropDown
                        disable={isLocked}
                        initialState={getManualExternalOrderDropdownValue(form.deliveryType)}
                        data={createManualExternalOrderDropdownOptions(
                          manualExternalOrderDeliveryOptions,
                          getManualExternalOrderDeliveryLabel,
                        )}
                        handlerFuncion={(event) =>
                          patch({ deliveryType: event.target.value.toNumber() as ManualExternalOrder["deliveryType"] })
                        }
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.select.select4"
                      />

                      <GenericDropDown
                        disable={isLocked}
                        initialState={getManualExternalOrderDropdownValue(form.reconciliationStatus)}
                        data={createManualExternalOrderDropdownOptions(
                          manualExternalOrderReconciliationOptions,
                          getManualExternalOrderReconciliationLabel,
                        )}
                        handlerFuncion={(event) =>
                          patch({
                            reconciliationStatus:
                              event.target.value.toNumber() as ManualExternalOrder["reconciliationStatus"],
                          })
                        }
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.select.select5"
                      />
                    </div>
                  </Animate>
                )}
              </section>

              <section className="grid gap-3">
                <SectionHeader
                  title={t("sections.financial")}
                  icon={faPenToSquare}
                  onToggle={() => setShowFinancial((current) => !current)}
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.section.financial"
                />

                {showFinancial && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <div className="grid grid-cols-1 gap-3 p-2 border-2 rounded-lg md:grid-cols-3">
                      <InputField
                        disable={isLocked}
                        icon={faBoxOpen}
                        type="number"
                        value={form.productsAmount.toStringOrEmpty()}
                        placeholder={t("fields.products_amount")}
                        onChange={(event) => handleMoneyChange("productsAmount", event.target.value)}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.productsAmount"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faTags}
                        type="number"
                        value={form.additionsAmount.toStringOrEmpty()}
                        placeholder={t("fields.additions_amount")}
                        onChange={(event) => handleMoneyChange("additionsAmount", event.target.value)}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.additionsAmount"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faTruck}
                        type="number"
                        value={form.deliveryFeeAmount.toStringOrEmpty()}
                        placeholder={t("fields.delivery_fee")}
                        onChange={(event) => handleMoneyChange("deliveryFeeAmount", event.target.value)}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.deliveryFee"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faPercent}
                        type="number"
                        value={form.discountAmount.toStringOrEmpty()}
                        placeholder={t("fields.discount")}
                        onChange={(event) => handleMoneyChange("discountAmount", event.target.value)}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.discount"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faStore}
                        type="number"
                        value={form.platformFeeAmount.toStringOrEmpty()}
                        placeholder={t("fields.platform_fee")}
                        onChange={(event) => handleMoneyChange("platformFeeAmount", event.target.value)}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.platformFee"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faCreditCard}
                        type="number"
                        value={form.paymentFeeAmount.toStringOrEmpty()}
                        placeholder={t("fields.payment_fee")}
                        onChange={(event) => handleMoneyChange("paymentFeeAmount", event.target.value)}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.paymentFee"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faCoins}
                        type="number"
                        value={form.otherCostsAmount.toStringOrEmpty()}
                        placeholder={t("fields.other_costs")}
                        onChange={(event) => handleMoneyChange("otherCostsAmount", event.target.value)}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.otherCosts"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faMoneyBillWave}
                        type="number"
                        value={form.netAmount.toStringOrEmpty()}
                        placeholder={t("fields.net_amount")}
                        onChange={(event) => handleMoneyChange("netAmount", event.target.value)}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.netAmount"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faCalendar}
                        type="date"
                        value={
                          form.expectedPaymentDate
                            ? parseDateWithoutTimezoneShift(form.expectedPaymentDate).toInputDate()
                            : ""
                        }
                        placeholder={t("fields.expected_payment_date")}
                        onChange={(event) => patch({ expectedPaymentDate: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.expectedPaymentDate"
                      />

                      <InputField
                        disable={isLocked}
                        icon={faCalendar}
                        type="date"
                        value={form.paidAt ? parseDateWithoutTimezoneShift(form.paidAt).toInputDate() : ""}
                        placeholder={t("fields.paid_at")}
                        onChange={(event) => patch({ paidAt: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.paidAt"
                      />
                    </div>
                  </Animate>
                )}
              </section>

              <section className="grid gap-3 p-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <SectionHeader
                      title={t("sections.items")}
                      icon={faBoxOpen}
                      onToggle={() => setShowItems((current) => !current)}
                      descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.section.items"
                    />
                  </div>

                  <ButtonIcon
                    sizeNumber={SIZE.buttonExpandIcon}
                    bgColor={THEME.colors.buttonBGPlusColor}
                    iconColor={THEME.colors.buttonFGPlusColor}
                    icon={faPlus}
                    onClick={handleAddItem}
                    disabled={isLocked}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.button.handleAddItem"
                  />
                </div>

                {showItems && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <div className="grid gap-2">
                      <CountElements
                        count={form.items.length}
                        title={"fields.products"}
                        boxShadow="none"
                        animationTypes={AnimationTypes.slideUpIn}
                        animationDuration="2s"
                      />

                      {form.items.map((item, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 gap-2 rounded-xl items-center bg-slate-50 p-2 md:grid-cols-[1fr_90px_120px_120px_42px]"
                        >
                          <InputField
                            disable={isLocked}
                            icon={faBoxOpen}
                            type="text"
                            value={item.productName}
                            placeholder={t("fields.product_name")}
                            onChange={(event) => handleItemChange(index, { productName: event.target.value })}
                            descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.productName"
                          />

                          <InputField
                            disable={isLocked}
                            icon={faHashtag}
                            type="number"
                            value={item.quantity.toStringOrEmpty()}
                            placeholder={t("fields.quantity")}
                            onChange={(event) => handleItemChange(index, { quantity: event.target.value.toNumber() })}
                            descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.quantity"
                          />

                          <InputField
                            disable={isLocked}
                            icon={faMoneyBill}
                            type="number"
                            value={item.unitPrice.toStringOrEmpty()}
                            placeholder={t("fields.unit_price")}
                            onChange={(event) => handleItemChange(index, { unitPrice: event.target.value.toNumber() })}
                            descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.unitPrice"
                          />

                          <div className="flex flex-row items-center gap-2">
                            <InputField
                              disable={isLocked}
                              icon={faReceipt}
                              type="number"
                              value={(item.totalPrice ?? 0).toStringOrEmpty()}
                              placeholder={t("fields.total_price")}
                              onChange={(event) =>
                                handleItemChange(index, { totalPrice: event.target.value.toNumber() })
                              }
                              descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.input.totalPrice"
                            />

                            <ButtonIcon
                              sizeNumber={SIZE.buttonExpandIcon}
                              bgColor={THEME.colors.buttonBGRemoveColor}
                              iconColor={THEME.colors.buttonFGRemoveColor}
                              icon={faMinus}
                              onClick={() => handleRemoveItem(index)}
                              disabled={isLocked}
                              size="small"
                              descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.button.handleRemoveItemIndex"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Animate>
                )}
              </section>
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className="flex flex-col justify-end gap-2 sm:flex-row ">
              <div className="flex flex-col gap-2 sm:flex-row">
                {isExisting && (
                  <>
                    <Button
                      label={t("actions.register")}
                      type="secondary"
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={() => setShowRegister(true)}
                      descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.button.register"
                    />

                    {!isLocked && (
                      <Button
                        label={t("actions.cancel")}
                        type="secondary"
                        bgColor={THEME.colors.buttonBGCloseColor}
                        onClick={() => onCancelOrder(form)}
                        descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.button.cancel"
                      />
                    )}
                  </>
                )}
              </div>

              {order?.status !== EManualExternalOrderStatus.Cancelled && (
                <Button
                  label={t("actions.save")}
                  typeButton="submit"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  disabled={isLocked}
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderPopup.button.save"
                />
              )}
            </div>

            {showRegister && isExisting && (
              <ViewRegister
                id={form.id}
                typeEntity="ManualExternalOrderEntityId"
                onClose={() => setShowRegister(false)}
              />
            )}
          </form>
        </Animate>
      </div>
    </ModalPortal>
  )
}
